/**
 * Build variable catalog for PowerPoint templates (browser; mirrors cloud-run-worker/presentationAiArtifacts.js).
 */

/**
 * @typedef {{ name: string, description: string, slideNumber?: number }} VariableCatalogEntry
 */

/**
 * Deduplicate catalog entries by variable name (first wins).
 * @param {VariableCatalogEntry[]} entries - Raw rows.
 * @returns {VariableCatalogEntry[]}
 */
function dedupeCatalogByName(entries) {
  const seen = new Set();
  const out = [];
  for (const e of entries) {
    if (!e?.name || seen.has(e.name)) continue;
    seen.add(e.name);
    out.push({
      name: e.name,
      description: (e.description || '').slice(0, 800),
      slideNumber: typeof e.slideNumber === 'number' ? e.slideNumber : undefined,
    });
  }
  return out;
}

/**
 * Collect all {{variable}} names from slide HTML.
 * @param {string} html - Slide content HTML.
 * @returns {string[]} Unique names in order of first appearance.
 */
function extractVariableNamesFromHtml(html) {
  const re = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
  const names = [];
  const seen = new Set();
  let m;
  while ((m = re.exec(html)) !== null) {
    if (!seen.has(m[1])) {
      seen.add(m[1]);
      names.push(m[1]);
    }
  }
  return names;
}

/**
 * @param {string} name - e.g. bullet_3_2
 * @param {string[]} runs - Full text runs for slide.
 * @param {number} slideIndex - 0-based.
 * @returns {string}
 */
function describeBulletVariable(name, runs, slideIndex) {
  const match = name.match(/^bullet_(\d+)_(\d+)$/);
  if (!match) return '';
  const slideNum = parseInt(match[1], 10);
  const bulletNum = parseInt(match[2], 10);
  if (slideNum !== slideIndex + 1) return '';
  const [title, ...bullets] = runs.length ? [runs[0], ...runs.slice(1)] : [];
  const src = bullets[bulletNum - 1];
  if (src) {
    return `Bullet ${bulletNum} under heading "${(title || '').slice(0, 120)}". Original line: "${src.slice(0, 280)}".`;
  }
  return `Bullet ${bulletNum} on slide ${slideNum} (no source line captured).`;
}

/**
 * Build variable catalog entries from slides that include `aiNotes.sourceFragments` or fall back to HTML only.
 * @param {{ content: string, aiNotes?: { slideNumber?: number, sourceFragments?: string[] } }[]} slides - Slides with optional aiNotes.
 * @returns {VariableCatalogEntry[]}
 */
export function buildVariableCatalog(slides) {
  const entries = [];

  for (let i = 0; i < slides.length; i++) {
    const slide = slides[i];
    const html = slide?.content || '';
    const names = extractVariableNamesFromHtml(html);
    const fragments = slide?.aiNotes?.sourceFragments || [];
    const slideNum = slide?.aiNotes?.slideNumber ?? i + 1;

    for (const name of names) {
      let description = '';
      if (name === 'presentation_title') {
        description = `Opening title (slide 1). Source hint: "${(fragments[0] || '').slice(0, 200)}".`;
      } else if (name === 'subtitle') {
        description = `Subtitle or strapline under the title. Source hint: "${(fragments[1] || fragments[0] || '').slice(0, 200)}".`;
      } else if (name.startsWith('slide_heading_')) {
        const idx = parseInt(name.replace('slide_heading_', ''), 10);
        description = `Heading for slide ${idx}. Source hint: "${(fragments[0] || '').slice(0, 200)}".`;
      } else if (name === 'slide_text') {
        description = `General body text for slide ${slideNum}. Source: "${fragments.join(' · ').slice(0, 300)}".`;
      } else if (name.startsWith('bullet_')) {
        description = describeBulletVariable(name, fragments, i) || `Variable ${name} on slide ${slideNum}.`;
      } else if (/^slide_\d+_overflow$/.test(name)) {
        const want = parseInt(name.replace(/^slide_(\d+)_overflow$/, '$1'), 10);
        const ov = slide?.aiNotes?.overflowText || '';
        if (want === slideNum && ov) {
          description = `Additional text from slide ${slideNum} that did not fit the main bullet list (summarize or shorten). Source excerpt: "${ov.slice(0, 400)}".`;
        } else {
          description = `Overflow / detail block for slide ${slideNum}.`;
        }
      } else {
        description = `Placeholder "${name}" on slide ${slideNum}. Nearby source context: "${fragments.join(' · ').slice(0, 250)}".`;
      }
      entries.push({ name, description, slideNumber: slideNum });
    }
  }

  return dedupeCatalogByName(entries);
}

/**
 * Merge saved or API-provided catalog rows onto fresh heuristic rows by variable name (hints win).
 * @param {{ name: string, description?: string, slideNumber?: number }[] | null} hintRows - Import / saved descriptions.
 * @param {VariableCatalogEntry[]} heuristicRows - From {@link buildVariableCatalog}.
 * @returns {VariableCatalogEntry[]}
 */
export function mergeVariableCatalogDescriptions(hintRows, heuristicRows) {
  if (!hintRows || hintRows.length === 0) return heuristicRows;
  const map = new Map(hintRows.filter((r) => r?.name).map((r) => [r.name, r]));
  return heuristicRows.map((h) => {
    const ai = map.get(h.name);
    if (ai && typeof ai.description === 'string' && ai.description.trim()) {
      return {
        name: h.name,
        description: ai.description.trim().slice(0, 900),
        slideNumber: typeof ai.slideNumber === 'number' ? ai.slideNumber : h.slideNumber,
      };
    }
    return h;
  });
}
